import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Clock, Check, ArrowLeft, ArrowRight, CalendarDays, Loader2 } from "lucide-react";

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function Booking() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      const pre = params.get("service");
      if (pre) {
        const found = r.data.find((s) => s.id === pre);
        if (found) {
          setService(found);
          setStep(2);
        }
      }
    });
  }, [params]);

  useEffect(() => {
    if (service && date) {
      setLoadingSlots(true);
      setTime(null);
      api
        .get("/slots", { params: { service_id: service.id, date: fmtDate(date) } })
        .then((r) => setSlots(r.data.slots))
        .catch(() => setSlots([]))
        .finally(() => setLoadingSlots(false));
    }
  }, [service, date]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        service_id: service.id,
        date: fmtDate(date),
        time,
        ...form,
      });
      setConfirmed(res.data);
      setStep(4);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const steps = ["Service", "Date & Time", "Details", "Done"];

  return (
    <div data-testid="booking-page" className="max-w-2xl mx-auto px-6 py-16 min-h-[70vh]">
      {/* Stepper */}
      <div className="flex items-center justify-center gap-2 mb-12">
        {steps.map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full text-sm transition-colors ${
                step >= i + 1 ? "bg-[#556B2F] text-white" : "bg-[#E9E0CC] text-[#5C5848]"
              }`}
            >
              {step > i + 1 ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            {i < steps.length - 1 && <div className={`w-8 h-px ${step > i + 1 ? "bg-[#556B2F]" : "bg-[#DAD0BA]"}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Service */}
      {step === 1 && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="step-service">
          <h1 className="font-serif text-4xl font-light text-[#33311F] mb-8 text-center">Choose a service</h1>
          <div className="space-y-4">
            {services.map((s) => (
              <button
                key={s.id}
                data-testid={`select-service-${s.id}`}
                onClick={() => {
                  setService(s);
                  setStep(2);
                }}
                className={`w-full text-left flex items-center gap-4 bg-[#FFFFFF] border rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(60,55,40,0.06)] ${
                  service?.id === s.id ? "border-[#556B2F]" : "border-[#DAD0BA]"
                }`}
              >
                <img src={s.image_url} alt={s.name} className="w-16 h-16 rounded-xl object-cover" />
                <div className="flex-1">
                  <h3 className="font-serif text-xl text-[#33311F]">{s.name}</h3>
                  <p className="text-sm text-[#5C5848] flex items-center gap-1.5 mt-1">
                    <Clock className="w-3.5 h-3.5" /> {s.duration_minutes} min discovery call
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#556B2F]" />
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Step 2: Date & Time */}
      {step === 2 && service && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="step-datetime">
          <h1 className="font-serif text-4xl font-light text-[#33311F] mb-2 text-center">Pick a date & time</h1>
          <p className="text-center text-[#5C5848] mb-8">{service.name} · {service.duration_minutes} min</p>
          <div className="bg-[#FFFFFF] border border-[#DAD0BA] rounded-2xl p-6 flex flex-col items-center">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              data-testid="date-picker"
            />
          </div>
          {date && (
            <div className="mt-8" data-testid="slots-container">
              <p className="text-sm uppercase tracking-[0.2em] text-[#556B2F] mb-4 flex items-center gap-2">
                <CalendarDays className="w-4 h-4" /> Available times
              </p>
              {loadingSlots ? (
                <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#556B2F]" /></div>
              ) : slots.length === 0 ? (
                <p data-testid="no-slots-message" className="text-[#5C5848] text-sm py-4">No available slots for this day. Please choose another date.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {slots.map((s) => (
                    <button
                      key={s}
                      data-testid={`slot-${s}`}
                      onClick={() => setTime(s)}
                      className={`py-2.5 rounded-full text-sm border transition-colors ${
                        time === s ? "bg-[#556B2F] text-white border-[#556B2F]" : "bg-[#FFFFFF] border-[#DAD0BA] text-[#33311F] hover:border-[#556B2F]"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between mt-10">
            <button data-testid="back-to-service" onClick={() => setStep(1)} className="flex items-center gap-2 text-[#5C5848] hover:text-[#33311F]">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button
              data-testid="datetime-next"
              disabled={!date || !time}
              onClick={() => setStep(3)}
              className="bg-[#556B2F] hover:bg-[#44531F] disabled:opacity-40 text-white rounded-full px-8 py-3 transition-colors flex items-center gap-2"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}

      {/* Step 3: Details */}
      {step === 3 && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="step-details">
          <h1 className="font-serif text-4xl font-light text-[#33311F] mb-8 text-center">Your details</h1>
          <div className="bg-[#FFFFFF] border border-[#DAD0BA] rounded-2xl p-6 mb-6 text-sm text-[#5C5848]">
            <div className="flex justify-between py-1"><span>Service</span><span className="text-[#33311F] font-medium">{service.name}</span></div>
            <div className="flex justify-between py-1"><span>Date</span><span className="text-[#33311F] font-medium">{fmtDate(date)}</span></div>
            <div className="flex justify-between py-1"><span>Time</span><span className="text-[#33311F] font-medium">{time}</span></div>
          </div>
          <div className="space-y-5">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="input-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1.5" placeholder="Jane Doe" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="input-email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1.5" placeholder="jane@email.com" />
            </div>
            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input id="phone" data-testid="input-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="mt-1.5" placeholder="+1 555 000 0000" />
            </div>
            <div>
              <Label htmlFor="notes">Anything you'd like me to know? (optional)</Label>
              <Textarea id="notes" data-testid="input-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" rows={3} />
            </div>
          </div>
          <div className="flex justify-between mt-10">
            <button data-testid="back-to-datetime" onClick={() => setStep(2)} className="flex items-center gap-2 text-[#5C5848] hover:text-[#33311F]">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button
              data-testid="confirm-booking"
              disabled={!form.customer_name || !form.customer_email || submitting}
              onClick={submit}
              className="bg-[#556B2F] hover:bg-[#44531F] disabled:opacity-40 text-white rounded-full px-8 py-3 transition-colors flex items-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Confirm Booking
            </button>
          </div>
        </motion.div>
      )}

      {/* Step 4: Confirmation */}
      {step === 4 && confirmed && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="step-confirmation" className="text-center">
          <div className="w-16 h-16 rounded-full bg-[#556B2F] flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-serif text-4xl font-light text-[#33311F]">You're all booked!</h1>
          <p className="text-[#5C5848] mt-3">A confirmation has been sent to {confirmed.customer_email}.</p>
          <div className="bg-[#FFFFFF] border border-[#DAD0BA] rounded-2xl p-6 mt-8 text-left max-w-md mx-auto">
            <div className="flex justify-between py-1.5 text-sm"><span className="text-[#5C5848]">Service</span><span className="text-[#33311F] font-medium">{confirmed.service_name}</span></div>
            <div className="flex justify-between py-1.5 text-sm"><span className="text-[#5C5848]">Date</span><span className="text-[#33311F] font-medium">{confirmed.date}</span></div>
            <div className="flex justify-between py-1.5 text-sm"><span className="text-[#5C5848]">Time</span><span className="text-[#33311F] font-medium">{confirmed.time}</span></div>
          </div>
          <Link to="/" data-testid="back-home" className="mt-8 inline-flex items-center gap-2 text-[#33311F] underline underline-offset-4 decoration-[#C0714A]">
            Back to home
          </Link>
        </motion.div>
      )}
    </div>
  );
}
